/**
 * ============================================================
 * LOGIN PAGE - User Authentication
 * ============================================================
 * 
 * This component handles user login with email or phone.
 * 
 * Features:
 * - Toggle between Email and Phone login
 * - Email/Phone validation
 * - Password hashing before API call
 * - Flash message display (from forgot password redirect)
 * - Redirects to dashboard on success
 * 
 * API Endpoint: POST /login
 * 
 * API Payload Format (current - mobile only):
 * {
 *   mobile: string (10 digits),
 *   password: string (hashed)
 * }
 * 
 * API Payload Format (future - when email supported):
 * {
 *   type: "mobile" | "email",
 *   value: string,
 *   password: string (hashed)
 * }
 * ============================================================
 */

import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button, Typography, Flex, Segmented, Alert } from "antd";
import loginImg from "../../assets/images/login-img.svg";
import CaptchaWidget from "../../components/Auth/CaptchaWidget";
import { config } from "../../config";

import "../../assets/styles/components_styles/validation.css";
import "../../assets/styles/components_styles/login.css";
import { isValidEmail, isValidPhone, getUserType, hashPassword } from "../../utils/appUtils";
import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import { VALIDATION_MESSAGES } from "../../utils/constants";
import { 
  loginUser, 
  selectLoginSuccess, 
  resetLoginSuccess, 
  selectLoginError, 
  clearLoginError, 
  selectLoginLoading 
} from "../../redux/userSlice";

const { Title, Text } = Typography;

export default function LoginPage() {
  // ========== REDUX HOOKS ==========
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loginSuccess = useSelector(selectLoginSuccess);  // true after successful login
  const loginError = useSelector(selectLoginError);      // API error message (incorrect password, etc.)
  const loginLoading = useSelector(selectLoginLoading);  // true while API is loading

  // ========== LOCAL STATE ==========

  // Flash message from forgot password redirect (passed via router state)
  const [successMessage, setSuccessMessage] = useState(
    location.state?.message || ""
  );

  const [errors, setErrors] = useState({});
  const [loginType, setLoginType] = useState("email");
  const [formData, setFormData] = useState({
    value: "",
    password: ""
  });
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);
  const requireCaptcha = !!config.turnstileSiteKey;

  /**
   * REDIRECT ON SUCCESS
   * After successful login, redirect to dashboard
   * Token is automatically stored by userSlice
   */
  useEffect(() => {
    if (loginSuccess) {
      dispatch(resetLoginSuccess());
      const activeRole = getUserType();
      if (activeRole === "organizer") {
        navigate("/dashboard");
      } else {
        if (location.state?.from) {
          navigate(location.state.from);
        } else {
          navigate("/");
        }
      }
    }
  }, [loginSuccess, navigate, dispatch]);

  useEffect(() => {
    if (loginError && requireCaptcha && captchaRef.current) captchaRef.current.reset();
  }, [loginError, requireCaptcha]);

  /**
   * AUTO-HIDE FLASH MESSAGE
   * Clear success message after 5 seconds
   */
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  /**
   * LOGIN TYPE TOGGLE HANDLER
   * Switches between email and phone login
   * Clears input value and errors when switching
   */
  const handleLoginTypeChange = (type) => {
    setLoginType(type);
    setFormData({ ...formData, value: "" });
    setErrors({});
  };

  /**
   * INPUT CHANGE HANDLER
   * Updates form state and clears field error on type
   */
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ""
      });
    }
    // Clear API error when user types
    if (loginError) {
      dispatch(clearLoginError());
    }
  };

  /**
   * FORM SUBMIT HANDLER
   * 
   * Validation:
   * 1. Value required (email or phone)
   * 2. Valid format based on type
   * 3. Password required
   * 
   * Then hashes password and dispatches loginUser action
   */
  const handleSubmit = async () => {
    // ---- VALIDATION ----

    // Check if value is empty
    if (!formData.value) {
      const errorMsg = loginType === "email"
        ? VALIDATION_MESSAGES.EMAIL_INVALID
        : VALIDATION_MESSAGES.PHONE_REQUIRED;
      setErrors({ value: errorMsg });
      return;
    }

    // Validate email format
    if (loginType === "email" && !isValidEmail(formData.value)) {
      setErrors({ value: VALIDATION_MESSAGES.EMAIL_INVALID });
      return;
    }

    // Validate phone format (10 digits)
    if (loginType === "phone" && !isValidPhone(formData.value)) {
      setErrors({ value: VALIDATION_MESSAGES.PHONE_INVALID });
      return;
    }

    // Check password
    if (!formData.password) {
      setErrors({ password: VALIDATION_MESSAGES.PASSWORD_REQUIRED });
      return;
    }

    setErrors({});  // Clear all errors

    // ---- SUBMIT TO API ----
    try {
      // Hash password before sending (security)
      const hashedPassword = await hashPassword(formData.password);

      if (requireCaptcha && !captchaToken) {
        setErrors({ password: "Please complete the verification." });
        return;
      }

      const submissionData = {
        type: loginType === "phone" ? "mobile" : "email",
        value: formData.value,
        password: hashedPassword,
        isAdmin: false
      };
      if (requireCaptcha) submissionData.captchaToken = captchaToken;

      dispatch(loginUser(submissionData));

    } catch (error) {
      console.error("Hashing error", error);
    }
  };

  // ========== RENDER ==========
  return (
    <div className="loginPageRoot">
      {/* SEO HELMET WRAPPER */}
      <HelmetWrapper
        title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
        description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
        and city-specific visibility for organizers and users." 
        keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
        online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
        Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
        Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
      /> 
      {/* HEADER / NAVBAR */}
      <Header isHome={false} />

      {/* MAIN CONTENT */}
      <main className="loginMain container">
        {/* Left Side - Image */}
        <div className="loginImage">
          <img src={loginImg} alt="Login" className="sideImage" />
        </div>

        {/* Right Side - Login Form */}
        <div className="loginFormWrap">
          <div className="loginCard">
            <div className="loginCardInner">
              {/* Header */}
              <div className="loginCardHeader">
                <Title level={3} className="title">
                  Discover exciting events near you
                </Title>
                <Text className="color-neutral">
                  Login to get exclusive offers.
                </Text>
              </div>

              {/* FLASH MESSAGE - Shows after password reset */}
              {successMessage && (
                <Alert
                  message={successMessage}
                  type="success"
                  showIcon
                  closable
                  onClose={() => setSuccessMessage("")}
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* LOGIN ERROR - Shows incorrect password or login failed */}
              {loginError && (
                <Alert
                  message={VALIDATION_MESSAGES.LOGIN_INCORRECT_PASSWORD}
                  type="error"
                  showIcon
                  closable
                  onClose={() => dispatch(clearLoginError())}
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* LOGIN TYPE TOGGLE - Email or Phone */}
              <div className="loginTypeToggle">
                <Segmented
                  options={[
                    {
                      label: (
                        <div className="segmentOption">
                          <span className="material-symbols-outlined">mail</span>
                          <span>Email</span>
                        </div>
                      ),
                      value: "email"
                    },
                    {
                      label: (
                        <div className="segmentOption">
                          <span className="material-symbols-outlined">phone</span>
                          <span>Phone</span>
                        </div>
                      ),
                      value: "phone"
                    }
                  ]}
                  value={loginType}
                  onChange={handleLoginTypeChange}
                  block
                  size="large"
                  className="loginSegmented"
                />
              </div>

              {/* LOGIN FORM */}
              <Form name="login" layout="vertical" onFinish={handleSubmit}>
                {/* DYNAMIC INPUT - Changes based on loginType */}
                <div className="loginInputWrapper">
                  {loginType === "email" ? (
                    // Email Input
                    <Form.Item
                      required
                      label="Email ID"
                      validateStatus={errors.value ? "error" : ""}
                      help={errors.value}
                      style={{ marginBottom: 12 }}
                      key="email-input"
                    >
                      <Input
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        prefix={
                          <span className="material-symbols-outlined prefix-blue">
                            mail
                          </span>
                        }
                        placeholder="you@example.com"
                        size="large"
                        className="hasPrefixIconInput"
                      />
                    </Form.Item>
                  ) : (
                    // Phone Input
                    <Form.Item
                      required
                      label="Phone Number"
                      validateStatus={errors.value ? "error" : ""}
                      help={errors.value}
                      style={{ marginBottom: 12 }}
                      key="phone-input"
                    >
                      <Input
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        prefix={
                          <span className="material-symbols-outlined prefix-blue">
                            phone
                          </span>
                        }
                        placeholder="Enter 10-digit phone number"
                        size="large"
                        className="hasPrefixIconInput"
                        maxLength={10}
                      />
                    </Form.Item>
                  )}
                </div>

                {/* Password Input */}
                <Form.Item
                  required
                  label="Password"
                  validateStatus={errors.password ? "error" : ""}
                  help={errors.password}
                  style={{ marginBottom: 12 }}
                >
                  <Input.Password
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    prefix={
                      <span className="material-symbols-outlined prefix-blue">
                        lock
                      </span>
                    }
                    placeholder="Enter password"
                    size="large"
                    className="hasPrefixIconInput"
                  />
                </Form.Item>

                <CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} />

                {/* Submit Button */}
                <Form.Item style={{ marginTop: 18 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    className="btnGradient"
                    loading={loginLoading}
                  >
                    {loginLoading ? 'Signing in...' : 'Sign in'}
                  </Button>
                </Form.Item>

                {/* Footer Links */}
                <Flex align="center" justify="space-between">
                  <Link className="loginForgot" to="/forgot-password">
                    Forgot password?
                  </Link>
                  <Link to="/signup">
                    <Button>Create account</Button>
                  </Link>
                </Flex>
              </Form>
            </div>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}
